import { useEffect, useState } from 'react';
import { reduxForm, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import useLabels from 'infrastructure/js/hooks/useLabels';
import Validation from 'infrastructure/js/components/controls/controlsValidations';
import Button from 'infrastructure/js/components/controls/Button/button';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox.js';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import PasswordField from 'infrastructure/js/components/controls/PasswordField/passwordField';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import { enumTypes, getLookupOptions } from '../../../../../../utils/enumHelper';
import ErrorIcon from '../../../../../../../assets/svg/error-icon.svg';
import AlertPopover from 'infrastructure/js/components/popover/AlertPopover/alertPopover.js';

import './createTrucksSensorConfigDialog.scss';

const maxLength256 = Validation.maxLength(256);

export function CreateTrucksSensorConfigDialog({
  sData,
  actions,
  handleSubmit,
  pristine,
  initialize,
  reloadParentComponent = null,
  provider,
  username,
  password,
  domain,
  sublicense,
}) {
  const labels = useLabels('mat.administration.settings.trucksSensors.dialog.createSensorConfig.');
  const dialogLabels = useLabels('mat.dialog.');
  const [eventProfilesError, setEventProfilesError] = useState(false);
  const isEditMode = !!sData.get('itemToEdit');
  const dialogData = sData.get('dialogData');
  const eventProfiles = sData.get('eventProfiles') || [];
  const titleText = isEditMode ? labels.get('header.title.edit') : labels.get('header.title.create');
  const sensorsProviderOptions = getLookupOptions(enumTypes.SENSOR_PROVIDER_TYPE);

  useEffect(() => {
    if (isEditMode) {
      const initialDate = {
        provider: sensorsProviderOptions.find((x) => x.value === dialogData?.providerType),
        configurationName: dialogData?.businessId,
        username: dialogData?.username,
        password: dialogData?.password,
        domain: dialogData?.domain,
        sublicense: dialogData?.sublicense,
        eventProfile: { value: dialogData?.eventProfileId, label: dialogData?.eventProfileName },
      };
      initialize(initialDate);
      return;
    }

    initialize({
      provider: sensorsProviderOptions[0],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (values) => {
    const submitData = {
      providerType: values.provider.value,
      businessId: values.configurationName,
      username: values.username,
      password: values.password,
      domain: values.domain,
      sublicense: values.sublicense,
      eventProfileId: values.eventProfile?.value,
      eventProfileName: values.eventProfile?.label,
    };

    isEditMode && (submitData.settingId = sData.get('itemToEdit').id);

    actions.submit(submitData, null, reloadParentComponent, { isEditMode });
  };

  const handleGetEventProfiles = () => {
    setEventProfilesError(false);

    actions
      .fetchEventProfiles({
        provider: provider.value?.toLowerCase(),
        username,
        password,
        domain,
        sublicense,
      })
      .then(({ success }) => {
        setEventProfilesError(!success);
      });
  };

  const footerButtons = {
    left: [
      {
        id: 'cancel',
        text: dialogLabels.get('cancel'),
        action: actions.hide,
      },
    ],
    right: [
      {
        id: 'submit',
        text: isEditMode ? dialogLabels.get('save') : dialogLabels.get('create'),
        bsStyle: 'primary',
        loading: sData.get('loading'),
        action: handleSubmit(onSubmit),
        disabled: (isEditMode && pristine) || sData.get('loading'),
      },
    ],
  };

  const isGetEventProfilesDisabled = !username || !password || !domain || !sublicense || !provider;

  return (
    <Dialog
      id="create-trucks-sensor-config-dialog"
      className="create-trucks-sensor-config-dialog"
      titleText={titleText}
      show={sData.get('show')}
      onHide={actions.hide}
      sData={sData}
      footerButtons={footerButtons}
      onEnterKeyPress={handleSubmit(onSubmit)}
    >
      <InputSection label={labels.get('provider') + '*'} htmlFor="provider" className="inline left-side">
        <Combobox id="provider" name="provider" options={sensorsProviderOptions} validate={Validation.required} />
      </InputSection>

      <InputSection label={labels.get('configurationName') + '*'} htmlFor="configurationName" className="inline right-side">
        <TextField id="configurationName" name="configurationName" validate={[Validation.required, maxLength256]} />
      </InputSection>

      <InputSection label={labels.get('userName') + '*'} htmlFor="username" className="inline left-side">
        <TextField id="username" name="username" validate={[Validation.required, maxLength256]} />
      </InputSection>

      <InputSection label={labels.get('password') + '*'} htmlFor="password" className="inline right-side">
        <PasswordField id="password" name="password" enableReveal={true} validate={[Validation.required, maxLength256]} autoComplete="new-password" />
      </InputSection>

      <InputSection label={labels.get('domain') + '*'} htmlFor="domain" className="inline left-side">
        <TextField id="domain" name="domain" validate={[Validation.required, maxLength256]} />
      </InputSection>

      <InputSection label={labels.get('sublicense') + '*'} htmlFor="sublicense" className="inline right-side">
        <TextField id="sublicense" name="sublicense" validate={[Validation.required, maxLength256]} />
      </InputSection>

      <InputSection label={labels.get('eventProfiles')} htmlFor="eventProfiles" className="inline left-side event-profiles-section">
        <div className="event-profiles-wrapper">
          <Combobox id="eventProfile" name="eventProfile" options={eventProfiles} />
          <Button
            id="eventProfiles-button"
            bsStyle="default"
            onClick={handleGetEventProfiles}
            disabled={isGetEventProfilesDisabled}
            className="get-event-profiles-btn border-btn"
          >
            {labels.get('getEventProfiles')}
          </Button>
          {eventProfilesError && (
            <AlertPopover popoverType={'error'} popoverComponent={<div>{labels.get('getEventProfilesError')}</div>}>
              <ErrorIcon className="error-icon" />
            </AlertPopover>
          )}
        </div>
      </InputSection>
    </Dialog>
  );
}

CreateTrucksSensorConfigDialog = reduxForm({
  form: 'CreateTrucksSensorConfigDialog',
})(CreateTrucksSensorConfigDialog);

const selector = formValueSelector('CreateTrucksSensorConfigDialog');

export default connect((state) => selector(state, 'username', 'password', 'domain', 'sublicense', 'provider'))(CreateTrucksSensorConfigDialog);

CreateTrucksSensorConfigDialog.propTypes = {
  sData: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func,
  pristine: PropTypes.bool,
  initialize: PropTypes.func,
  reloadParentComponent: PropTypes.func,
  provider: PropTypes.object,
  username: PropTypes.string,
  password: PropTypes.string,
  domain: PropTypes.string,
  sublicense: PropTypes.string,
};
